// window.mytheseus, runtime API 1 (DESIGN §3.6), as template-web/mt/runtime.js
// expects it. Defined before the build's entry module is imported.

import { ApiError, getJSON, postJSON } from '../lib/api.js';
import { createCalendarSignal } from '../lib/calendarsignal.js';

/** base64 of a Blob, ArrayBuffer or typed array (JPEG/PNG for the AI route). */
export async function toBase64(image) {
  let bytes;
  if (image == null) return null;
  if (typeof Blob !== 'undefined' && image instanceof Blob) bytes = new Uint8Array(await image.arrayBuffer());
  else if (image instanceof ArrayBuffer) bytes = new Uint8Array(image);
  else if (ArrayBuffer.isView(image)) bytes = new Uint8Array(image.buffer, image.byteOffset, image.byteLength);
  else if (typeof image === 'string') return image.replace(/^data:[^,]*,/, '');
  else throw Object.assign(new Error('Unsupported image'), { code: 'malformed' });
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * The object the app sees. `info` = {name, edition, version_name,
 * running_code}; onReady() is called once, on the first ready().
 */
export function createRuntimeAPI({ info, onReady, openCoding, request = { getJSON, postJSON }, onCalendarUse = () => {}, onCalendarChange = () => {}, calendarSignal = createCalendarSignal() }) {
  let readied = false;
  const api = {
    api_version: 1,
    ready() {
      if (readied) return;
      readied = true;
      try { onReady(); } catch (e) { console.error(e); }
    },
    app: Object.freeze({ ...info }),
    openCoding() { openCoding(); },
    ai: Object.freeze({
      async status() {
        const s = await request.getJSON('/v1/app/ai');
        return { enabled: s?.enabled === true, max_prompt_chars: s?.max_prompt_chars, max_image_bytes: s?.max_image_bytes };
      },
      async generate(prompt, options = {}) {
        const body = { prompt: String(prompt ?? '') };
        const image = await toBase64(options.image);
        if (image) body.image_base64 = image;
        // Generation can take a while; the server bounds it, the app's signal can stop it.
        const out = await request.postJSON('/v1/app/ai', body, { signal: options.signal, timeout: 120_000 });
        return typeof out?.text === 'string' ? out.text : '';
      },
    }),
    calendar: Object.freeze({
      async status() {
        onCalendarUse();
        // A read overtaken by a confirmed disconnect describes the grant as it
        // was, so it is read once more. If that read is overtaken too, refuse
        // rather than answer with a state this page knows is out of date.
        for (let attempt = 0; attempt < 2; attempt++) {
          const startedAt = calendarSignal.revision();
          const out = await request.getJSON('/v1/app/google/calendar');
          if (calendarSignal.revision() !== startedAt) continue;
          calendarSignal.observe(out, startedAt);
          return out;
        }
        throw new ApiError('google_unavailable', 'Google Calendar access changed while checking. Try again.');
      },
      async connect(scopes) {
        onCalendarUse();
        const out = await request.postJSON('/v1/app/google/calendar/connect', scopes ? { scopes } : {});
        // Server creates the exact shared consent URL. Only an explicit user
        // action should call connect; no popup or provider token in app code.
        globalThis.location.assign(out.url);
      },
      calendars: (pageToken, options = {}) => {
        onCalendarUse();
        return request.getJSON('/v1/app/google/calendar/calendars',
          { signal: options.signal, params: { page_token: pageToken } });
      },
      events: ({ calendarId = 'primary', timeMin, timeMax, pageToken, signal } = {}) => {
        onCalendarUse();
        return request.getJSON('/v1/app/google/calendar/events', { signal,
          params: { calendar_id: calendarId, time_min: timeMin, time_max: timeMax, page_token: pageToken } });
      },
      async disconnect() {
        onCalendarUse();
        try {
          const out = await request.postJSON('/v1/app/google/calendar/disconnect', {});
          if (out?.connected === false) calendarSignal.disconnected(out);
          return out;
        } finally { onCalendarChange(); }
      },
      // listener({connected: false, revocation_pending}) when this page
      // verifies the app lost Calendar access; returns unsubscribe().
      subscribe: (listener) => calendarSignal.subscribe(listener),
    }),
    storage: Object.freeze({
      async persist() {
        try {
          const s = globalThis.navigator?.storage;
          if (typeof s?.persisted === 'function' && (await s.persisted())) return true;
          return typeof s?.persist === 'function' ? (await s.persist()) === true : false;
        } catch {
          return false;
        }
      },
    }),
  };
  return Object.freeze(api);
}

/** Installs it as window.mytheseus (read-only to the app). */
export function installRuntimeAPI(api, target = globalThis) {
  Object.defineProperty(target, 'mytheseus', { value: api, writable: false, enumerable: true, configurable: true });
}

// ---- the boot watch (R1b, §2.5) ----

export const READY_TIMEOUT = 8_000;

const BENIGN = /^ResizeObserver loop/;

/**
 * Watches for a build failing to start: an uncaught error or rejection
 * before ready(), or no ready() within 8 s of visible time. Calls
 * onFail(text) at most once; stop() ends the watch (on ready()).
 */
export function watchBoot({ onFail, target = globalThis, doc = globalThis.document, origin = globalThis.location?.origin, timeout = READY_TIMEOUT }) {
  let done = false;
  let timer = null;
  const fail = (text) => { if (done) return; stop(); onFail(text); };
  const onError = (e) => {
    const msg = e?.error?.message || e?.message || '';
    if (BENIGN.test(msg)) return;
    // A browser extension's script is not the app's failure.
    if (e?.filename && origin && !String(e.filename).startsWith(origin)) return;
    const name = e?.error?.name && e.error.name !== 'Error' ? `${e.error.name}: ` : '';
    fail(`${name}${msg || 'Unknown error'}`);
  };
  const onRejection = (e) => {
    const r = e?.reason;
    const text = r instanceof Error ? `${r.name && r.name !== 'Error' ? `${r.name}: ` : ''}${r.message}` : String(r ?? 'Unhandled rejection');
    fail(text);
  };
  // Time only counts while the page is visible: a background tab may not paint.
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (doc?.visibilityState === 'hidden') return;
      fail(`It didn't finish starting within ${Math.round(timeout / 1000)} seconds.`);
    }, timeout);
  };
  const onVisible = () => { if (doc.visibilityState !== 'hidden') arm(); else clearTimeout(timer); };
  target.addEventListener?.('error', onError);
  target.addEventListener?.('unhandledrejection', onRejection);
  doc?.addEventListener?.('visibilitychange', onVisible);
  arm();
  function stop() {
    done = true;
    clearTimeout(timer);
    target.removeEventListener?.('error', onError);
    target.removeEventListener?.('unhandledrejection', onRejection);
    doc?.removeEventListener?.('visibilitychange', onVisible);
  }
  return { fail, stop };
}
